import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Eye,
  EyeOff,
  Link,
  FolderPlus,
} from "lucide-react";

// Helper functions
function flattenCategories(categories) {
  let result = [];
  for (const cat of categories) {
    result.push({ id: cat.id, name: cat.name });
    if (cat.children && cat.children.length > 0) {
      result = result.concat(flattenCategories(cat.children));
    }
  }
  return result;
}

function getCategoryActions(category) {
  const actions =
    category.categoryCode === "ROOT"
      ? ["Associate Template", "Add Sub-Category", "Edit Template"]
      : [
          "Associate Template",
          "Edit Template",
          "Add Category",
          "Edit Category",
          "Add Sub-Category",
          "Delete Category",
          // Show only one action based on isHidden property
          category.isHidden ? "Mark As Unhidden" : "Mark As Hidden",
        ];
  return actions;
}

function renderCategoryAccordion(
  categories,
  onSelect,
  selectedId,
  onDelete,
  onToggleHide,
  setUserSelectedCategory,
  handleCategoryFormAction,
  level = 0
) {
  // If no categories or all categories have no children, render as a flat list
  if (!categories.some((cat) => cat.children && cat.children.length > 0)) {
    return (
      <div className="flex flex-col gap-1">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`flex items-center px-2 py-1${
              cat.hidden ? " opacity-50" : ""
            }${level > 0 ? ` ml-${Math.min(level * 6, 12)}` : ""}`}
          >
            <span className="flex items-center gap-2">
              {cat.name}
              <input
                type="radio"
                name="program-category-radio"
                checked={selectedId === cat.id}
                onChange={(e) => {
                  onSelect(cat.id);
                  setUserSelectedCategory(true);
                }}
                onClick={(e) => e.stopPropagation()}
                className="ml-2"
              />
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="p-1 ml-2">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {getCategoryActions(cat).map((action) => (
                  <DropdownMenuItem
                    key={action}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle different actions based on the action name
                      switch (action) {
                        case "Edit Category":
                          handleCategoryFormAction("Edit Category", cat);
                          break;
                        case "Edit Template":
                          // TODO: Implement edit template action
                          console.log(
                            "Edit Template clicked for category:",
                            cat.name
                          );
                          break;
                        case "Delete Category":
                          onDelete(cat);
                          break;
                        case "Mark As Hidden":
                        case "Mark As Unhidden":
                          onToggleHide(cat);
                          break;
                        case "Associate Template":
                          // TODO: Implement associate template action
                          console.log(
                            `${action} clicked for category:`,
                            cat.name
                          );
                          break;
                        case "Add Category":
                          handleCategoryFormAction("Add Category", cat);
                          break;
                        case "Add Sub-Category":
                          handleCategoryFormAction("Add Sub-Category", cat);
                          break;
                        default:
                          console.log(`Action ${action} not implemented yet`);
                      }
                    }}
                  >
                    {action === "Edit Category" ||
                    action === "Edit Template" ? (
                      <Edit className="w-4 h-4 mr-2" />
                    ) : action === "Delete Category" ? (
                      <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                    ) : action === "Mark As Hidden" ? (
                      <EyeOff className="w-4 h-4 mr-2" />
                    ) : action === "Mark As Unhidden" ? (
                      <Eye className="w-4 h-4 mr-2" />
                    ) : action === "Associate Template" ? (
                      <Link className="w-4 h-4 mr-2" />
                    ) : action === "Add Category" ? (
                      <FolderPlus className="w-4 h-4 mr-2" />
                    ) : action === "Add Sub-Category" ? (
                      <Plus className="w-4 h-4 mr-2" />
                    ) : null}
                    {action}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    );
  }
  // Otherwise, render as accordion
  return (
    <Accordion type="multiple" className="w-full">
      {categories.map((cat) => (
        <AccordionItem
          key={cat.id}
          value={cat.id}
          className={cat.hidden ? "opacity-50" : undefined}
        >
          <AccordionTrigger
            className={`flex items-center gap-2 px-2 py-1 ${
              level > 0 ? `ml-${Math.min(level * 6, 12)}` : ""
            }`}
          >
            <div className="flex items-center flex-1 justify-between w-full">
              <span className="flex items-center gap-2">
                {cat.name}
                <input
                  type="radio"
                  name="program-category-radio"
                  checked={selectedId === cat.id}
                  onChange={(e) => {
                    onSelect(cat.id);
                    setUserSelectedCategory(true);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="ml-2"
                />
              </span>
              <div className="flex items-center gap-1 ml-auto">
                {/* Accordion arrow is rendered by AccordionTrigger by default at the end */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="p-1 ml-2">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {getCategoryActions(cat).map((action) => (
                      <DropdownMenuItem
                        key={action}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle different actions based on the action name
                          switch (action) {
                            case "Edit Category":
                              handleCategoryFormAction("Edit Category", cat);
                              break;
                            case "Edit Template":
                              // TODO: Implement edit template action
                              console.log(
                                "Edit Template clicked for category:",
                                cat.name
                              );
                              break;
                            case "Delete Category":
                              onDelete(cat);
                              break;
                            case "Mark As Hidden":
                            case "Mark As Unhidden":
                              onToggleHide(cat);
                              break;
                            case "Associate Template":
                              // TODO: Implement associate template action
                              console.log(
                                `${action} clicked for category:`,
                                cat.name
                              );
                              break;
                            case "Add Category":
                              handleCategoryFormAction("Add Category", cat);
                              break;
                            case "Add Sub-Category":
                              handleCategoryFormAction("Add Sub-Category", cat);
                              break;
                            default:
                              console.log(
                                `Action ${action} not implemented yet`
                              );
                          }
                        }}
                      >
                        {action === "Edit Category" ||
                        action === "Edit Template" ? (
                          <Edit className="w-4 h-4 mr-2" />
                        ) : action === "Delete Category" ? (
                          <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                        ) : action === "Mark As Hidden" ? (
                          <EyeOff className="w-4 h-4 mr-2" />
                        ) : action === "Mark As Unhidden" ? (
                          <Eye className="w-4 h-4 mr-2" />
                        ) : action === "Associate Template" ? (
                          <Link className="w-4 h-4 mr-2" />
                        ) : action === "Add Category" ? (
                          <FolderPlus className="w-4 h-4 mr-2" />
                        ) : action === "Add Sub-Category" ? (
                          <Plus className="w-4 h-4 mr-2" />
                        ) : null}
                        {action}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {cat.children.length > 0 &&
              renderCategoryAccordion(
                cat.children,
                onSelect,
                selectedId,
                onDelete,
                onToggleHide,
                setUserSelectedCategory,
                handleCategoryFormAction,
                level + 1
              )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

interface CategoryTreeProps {
  categories: any[];
  loading: boolean;
  search: string;
  setSearch: (value: string) => void;
  selectedCategoryId: string;
  setSelectedCategoryId: (id: string) => void;
  setUserSelectedCategory: (value: boolean) => void;
  handleDeleteCategory: (cat: any) => void;
  handleToggleHide: (cat: any) => void;
  handleCategoryFormAction: (action: string, category: any) => void;
}

export const CategoryTree: React.FC<CategoryTreeProps> = ({
  categories,
  loading,
  search,
  setSearch,
  selectedCategoryId,
  setSelectedCategoryId,
  setUserSelectedCategory,
  handleDeleteCategory,
  handleToggleHide,
  handleCategoryFormAction,
}) => {
  return (
    <div className="flex-1 min-h-0">
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading...</div>
      ) : categories.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No categories found. Categories count: {categories.length}
        </div>
      ) : (
        renderCategoryAccordion(
          categories,
          setSelectedCategoryId,
          selectedCategoryId,
          handleDeleteCategory,
          handleToggleHide,
          setUserSelectedCategory,
          handleCategoryFormAction
        )
      )}
    </div>
  );
};

export { flattenCategories, getCategoryActions, renderCategoryAccordion };
